/**
 * ValidationPanel Component
 * Displays workflow validation results in a collapsible panel
 */

import { useMemo, useState, useEffect, useRef } from 'react'
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Chip,
  Collapse,
  Paper,
  IconButton,
  Divider,
  Button,
} from '@mui/material'
import BuildIcon from '@mui/icons-material/Build'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import ErrorIcon from '@mui/icons-material/Error'
import WarningIcon from '@mui/icons-material/Warning'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import CloseIcon from '@mui/icons-material/Close'
import LinkIcon from '@mui/icons-material/Link'
import SettingsIcon from '@mui/icons-material/Settings'
import AccountTreeIcon from '@mui/icons-material/AccountTree'
import { useWorkflowStore } from '@store/workflowStore'
import {
  ValidationIssue,
  ValidationCategory,
  ValidationSeverity,
} from '@features/adventures/types/validation.types'

/** Statistics from a successful compile */
export interface CompileStatistics {
  nodeCount: number
  edgeCount: number
  conditionCount: number
  configCount: number
}

interface ValidationPanelProps {
  onClose: () => void
  onNodeClick?: (nodeId: string) => void
  /** Source of validation: 'local' for client-side, 'compile' for server compilation */
  source?: 'local' | 'compile'
  /** Compile statistics (only shown when source is 'compile') */
  compileStats?: CompileStatistics
  /** Compiled version number (only shown when source is 'compile') */
  versionNumber?: number
  /** Callback for "Start Testing" button (only shown when compile succeeds) */
  onStartTesting?: () => void
  /** Whether the adventure is in a state where testing can start */
  canStartTesting?: boolean
}

export function ValidationPanel({
  onClose,
  onNodeClick,
  source = 'local',
  compileStats,
  versionNumber,
  onStartTesting,
  canStartTesting = false,
}: ValidationPanelProps) {
  const isCompileSource = source === 'compile'
  const validationResult = useWorkflowStore(state => state.validationResult)
  const [expandedCategories, setExpandedCategories] = useState<Set<ValidationCategory>>(
    new Set([ValidationCategory.Connectivity, ValidationCategory.Configuration, ValidationCategory.GraphStructure])
  )
  const onCloseRef = useRef(onClose)

  // Keep ref updated
  useEffect(() => {
    onCloseRef.current = onClose
  }, [onClose])

  // Auto-close after 5 seconds when LOCAL validation passes with no warnings
  // Don't auto-close compile results - user needs "Start Testing" button
  useEffect(() => {
    if (!isCompileSource && validationResult?.isValid && validationResult.warningCount === 0) {
      const timer = setTimeout(() => {
        onCloseRef.current()
      }, 5000)
      return () => clearTimeout(timer)
    }
  }, [isCompileSource, validationResult?.isValid, validationResult?.warningCount])

  if (!validationResult) {
    return null
  }

  // Group issues by category
  const groupedIssues = useMemo(() => {
    const groups = new Map<ValidationCategory, ValidationIssue[]>()

    for (const issue of [...validationResult.errors, ...validationResult.warnings]) {
      const existing = groups.get(issue.category) || []
      existing.push(issue)
      groups.set(issue.category, existing)
    }

    return groups
  }, [validationResult])

  const toggleCategory = (category: ValidationCategory) => {
    setExpandedCategories(prev => {
      const newExpanded = new Set(prev)
      if (newExpanded.has(category)) {
        newExpanded.delete(category)
      } else {
        newExpanded.add(category)
      }
      return newExpanded
    })
  }

  const getCategoryLabel = (category: ValidationCategory) => {
    switch (category) {
      case ValidationCategory.Connectivity:
        return 'Connectivity'
      case ValidationCategory.Configuration:
        return 'Configuration'
      case ValidationCategory.GraphStructure:
        return 'Graph Structure'
    }
  }

  const getCategoryIcon = (category: ValidationCategory) => {
    switch (category) {
      case ValidationCategory.Connectivity:
        return <LinkIcon fontSize="small" />
      case ValidationCategory.Configuration:
        return <SettingsIcon fontSize="small" />
      case ValidationCategory.GraphStructure:
        return <AccountTreeIcon fontSize="small" />
    }
  }

  const handleIssueClick = (issue: ValidationIssue) => {
    if (issue.nodeId && onNodeClick) {
      onNodeClick(issue.nodeId)
    }
  }

  const totalIssues = validationResult.errorCount + validationResult.warningCount

  return (
    <Paper
      sx={{
        position: 'absolute',
        bottom: 16,
        right: 16,
        left: { xs: 16, sm: 'auto' },
        width: { xs: 'auto', sm: 400 },
        maxWidth: { xs: 'calc(100% - 32px)', sm: 400 },
        maxHeight: 450,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        zIndex: 1000,
      }}
      elevation={6}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 1.5,
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: validationResult.isValid
            ? (validationResult.warningCount > 0 ? 'warning.main' : 'success.main')
            : 'error.main',
          color: 'white',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {isCompileSource ? (
            validationResult.isValid ? <BuildIcon /> : <ErrorIcon />
          ) : (
            validationResult.isValid ? (
              validationResult.warningCount > 0 ? <WarningIcon /> : <CheckCircleIcon />
            ) : (
              <ErrorIcon />
            )
          )}
          <Typography variant="subtitle1" fontWeight={600}>
            {isCompileSource
              ? (validationResult.isValid ? 'Compilation Successful' : 'Compilation Failed')
              : (validationResult.isValid
                ? (validationResult.warningCount > 0 ? 'Validation Warnings' : 'Validation Passed')
                : 'Validation Issues')}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {isCompileSource && versionNumber !== undefined && (
            <Chip
              size="small"
              icon={<BuildIcon sx={{ fontSize: 14, color: 'inherit !important' }} />}
              label={`v${versionNumber}`}
              sx={{
                bgcolor: 'rgba(255,255,255,0.2)',
                color: 'white',
                fontWeight: 'bold',
              }}
            />
          )}
          {validationResult.errorCount > 0 && (
            <Chip
              size="small"
              label={`${validationResult.errorCount} error${validationResult.errorCount !== 1 ? 's' : ''}`}
              sx={{
                bgcolor: 'rgba(255,255,255,0.2)',
                color: 'white',
                fontWeight: 'bold',
              }}
            />
          )}
          {validationResult.warningCount > 0 && (
            <Chip
              size="small"
              label={`${validationResult.warningCount} warning${validationResult.warningCount !== 1 ? 's' : ''}`}
              sx={{
                bgcolor: 'rgba(255,255,255,0.2)',
                color: 'white',
                fontWeight: 'bold',
              }}
            />
          )}
          <IconButton size="small" onClick={onClose} sx={{ color: 'white' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {/* Content */}
      <Box sx={{ overflow: 'auto', flex: 1 }}>
        {validationResult.isValid && validationResult.warningCount === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <CheckCircleIcon sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
            <Typography variant="body1" color="text.secondary">
              {isCompileSource ? 'Workflow compiled successfully!' : 'All validation checks passed!'}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              {isCompileSource
                ? 'Your workflow is ready for testing.'
                : 'Your workflow is ready to be compiled.'}
            </Typography>
            {/* Compile statistics */}
            {isCompileSource && compileStats && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  gap: 2,
                  mt: 2,
                  p: 1.5,
                  bgcolor: 'grey.50',
                  borderRadius: 1,
                }}
              >
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h6">{compileStats.nodeCount}</Typography>
                  <Typography variant="caption" color="text.secondary">Nodes</Typography>
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h6">{compileStats.edgeCount}</Typography>
                  <Typography variant="caption" color="text.secondary">Edges</Typography>
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h6">{compileStats.conditionCount}</Typography>
                  <Typography variant="caption" color="text.secondary">Conditions</Typography>
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h6">{compileStats.configCount}</Typography>
                  <Typography variant="caption" color="text.secondary">Configs</Typography>
                </Box>
              </Box>
            )}
            {!isCompileSource && (
              <Typography variant="caption" color="text.disabled" sx={{ mt: 2, display: 'block', fontStyle: 'italic' }}>
                Closing automatically in 5 seconds...
              </Typography>
            )}
          </Box>
        ) : (
          <List disablePadding>
            {Array.from(groupedIssues.entries()).map(([category, issues], index) => {
              const errorCount = issues.filter(i => i.severity === ValidationSeverity.Error).length
              const warningCount = issues.filter(i => i.severity === ValidationSeverity.Warning).length

              return (
                <Box key={category}>
                  <ListItemButton
                    onClick={() => toggleCategory(category)}
                    sx={{
                      bgcolor: 'action.hover',
                      borderBottom: 1,
                      borderColor: 'divider',
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      {getCategoryIcon(category)}
                    </ListItemIcon>
                    <ListItemText
                      primary={getCategoryLabel(category)}
                      secondary={
                        <Box component="span" sx={{ display: 'flex', gap: 0.5 }}>
                          {errorCount > 0 && (
                            <Typography variant="caption" color="error.main">
                              {errorCount} error{errorCount !== 1 ? 's' : ''}
                            </Typography>
                          )}
                          {errorCount > 0 && warningCount > 0 && (
                            <Typography variant="caption" color="text.secondary">/</Typography>
                          )}
                          {warningCount > 0 && (
                            <Typography variant="caption" color="warning.main">
                              {warningCount} warning{warningCount !== 1 ? 's' : ''}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                    {expandedCategories.has(category) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </ListItemButton>

                  <Collapse in={expandedCategories.has(category)}>
                    <List disablePadding>
                      {issues.map((issue) => (
                        <ListItem
                          key={issue.id}
                          disablePadding
                          sx={{
                            borderLeft: 3,
                            borderColor: issue.severity === ValidationSeverity.Error ? 'error.main' : 'warning.main',
                          }}
                        >
                          <ListItemButton
                            onClick={() => handleIssueClick(issue)}
                            disabled={!issue.nodeId}
                            sx={{
                              py: 1,
                              pl: 2,
                              '&:hover': {
                                bgcolor: issue.nodeId ? 'action.hover' : 'transparent',
                              },
                            }}
                          >
                            <ListItemIcon sx={{ minWidth: 32 }}>
                              {issue.severity === ValidationSeverity.Error ? (
                                <ErrorIcon color="error" fontSize="small" />
                              ) : (
                                <WarningIcon color="warning" fontSize="small" />
                              )}
                            </ListItemIcon>
                            <ListItemText
                              primary={issue.message}
                              secondary={
                                <Box component="span" sx={{ display: 'block' }}>
                                  {issue.nodeLabel && (
                                    <Typography
                                      component="span"
                                      variant="caption"
                                      color="primary"
                                      sx={{ display: 'block' }}
                                    >
                                      Node: {issue.nodeLabel}
                                    </Typography>
                                  )}
                                  {issue.details && (
                                    <Typography
                                      component="span"
                                      variant="caption"
                                      color="text.secondary"
                                      sx={{ display: 'block' }}
                                    >
                                      {issue.details}
                                    </Typography>
                                  )}
                                </Box>
                              }
                              primaryTypographyProps={{
                                variant: 'body2',
                                sx: { fontWeight: 500 },
                              }}
                            />
                          </ListItemButton>
                        </ListItem>
                      ))}
                    </List>
                  </Collapse>
                  {index < groupedIssues.size - 1 && <Divider />}
                </Box>
              )
            })}
          </List>
        )}
      </Box>

      {/* Footer */}
      {totalIssues > 0 ? (
        <Box
          sx={{
            p: 1,
            borderTop: 1,
            borderColor: 'divider',
            bgcolor: 'grey.50',
          }}
        >
          <Typography variant="caption" color="text.secondary">
            Click on an issue to select the node in the editor
          </Typography>
        </Box>
      ) : isCompileSource && validationResult.isValid && onStartTesting ? (
        <Box
          sx={{
            p: 1.5,
            borderTop: 1,
            borderColor: 'divider',
            bgcolor: 'grey.50',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 1,
          }}
        >
          <Button
            variant="contained"
            color="primary"
            startIcon={<PlayArrowIcon />}
            onClick={() => {
              onStartTesting()
              onClose()
            }}
            disabled={!canStartTesting}
          >
            Start Testing
          </Button>
        </Box>
      ) : null}
    </Paper>
  )
}

export default ValidationPanel
